import React from "react";
import mazakMark from "@/components/icons/oem/mazak-m.svg";
import doosanMark from "@/components/icons/oem/doosan-puma.svg";
import hwacheonMark from "@/components/icons/oem/hwacheon.svg";
import { categoryLabel } from "@/components/maintenance/lib";

// Machine identity — the module's one rule: OEM mark → machine NAME → ME-code
// carved chip. Never lead with the ME code (MAINTENANCE_MODULE_PLAN.md §2.6).
// Real brand marks are vendored as SVGs (Stock Center flags pattern); every
// other OEM falls back to a quiet monogram until its logo is supplied.

const MARKS = {
  MAZAK: { src: mazakMark, w: 17, h: 17 },
  DOOSAN: { src: doosanMark, w: 18, h: 18 },
  HWACHEON: { src: hwacheonMark, w: 19, h: 8 },
};

const MONOGRAM = {
  ACE: "ACE", DMG: "DMG", NISSIN: "NSN", HURCO: "HRC", AMS: "AMS",
  "GEDEE WEILER": "GW", "HYUNDAI-KIA": "HK", "HYUNDAi-KIA": "HK",
};

const monogramFor = (make) => {
  const key = String(make || "").toUpperCase().trim();
  if (MONOGRAM[key]) return MONOGRAM[key];
  return key.replace(/[^A-Z]/g, "").slice(0, 3) || "?";
};

/** The white well the mark sits in — quiet, so the machine NAME carries the row. */
export function MachineMark({ make, size = 28, className = "" }) {
  const key = String(make || "").toUpperCase().trim();
  const mark = MARKS[key];
  const scale = size / 28;
  return (
    <span
      className={`rl-oem-well ${className}`}
      style={{ width: size, height: size }}
      title={make || "Machine"}
      data-testid={`oem-mark-${key.toLowerCase() || "unknown"}`}
    >
      {mark ? (
        <img src={mark.src} alt="" aria-hidden="true" draggable={false}
             style={{ width: Math.round(mark.w * scale), height: Math.round(mark.h * scale) }} />
      ) : (
        <span className="rl-oem-mono" style={{ fontSize: Math.max(7, 8.5 * scale) }}>{monogramFor(make)}</span>
      )}
    </span>
  );
}

/** mark + name + ME chip (+ the category sub-line where there's room). */
export default function MachineIdentity({ machine, size = 28, showCategory = true, className = "" }) {
  if (!machine) return null;
  const cat = showCategory ? categoryLabel(machine.category) : "";
  return (
    <div className={`flex items-center gap-[9px] min-w-0 ${className}`} data-testid="machine-identity">
      <MachineMark make={machine.make} size={size} />
      <div className="min-w-0">
        <div className="flex items-center gap-[7px] min-w-0">
          <span className="text-[14px] font-bold tracking-[-0.01em] truncate">{machine.name || machine.machine_no}</span>
          <span className="rl-me-chip rl-carved shrink-0">{machine.machine_no}</span>
        </div>
        {cat && <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] truncate">{cat}</div>}
      </div>
    </div>
  );
}
